import { FormProvider, useForm } from 'react-hook-form';

import { Grid, Stack } from '@mui/material';

import { ICustodyActionReq } from '@/api';
import { RenderFormField } from '@/components';
import { ConfirmTypes, useToastStore } from '@/store';
import { useUserStore } from '@/store/store.user';

import { CustodyActionSection } from './CustodyActionSection';
import { ReleaseFormFields } from './data';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import {
  getCustodyActionDefaultValues,
  normalizeCustodyActionDateTime,
  useCustodyOfficerOptions,
} from './useCustodyActionForm';
import { CustodyActions } from '../../data';
import { useKasePropertyContext } from '../../PropertyContext';

export interface IReleaseForm {
  custodyAction: string;
}

export const ReleaseForm = ({ custodyAction }: IReleaseForm) => {
  const { updateToast } = useToastStore();
  const { currentPropertyId, custodyModalValues, parentId, sourceAdapter } =
    useKasePropertyContext();
  const { user } = useUserStore();
  const { officerOptions } = useCustodyOfficerOptions();

  const methods = useForm<ICustodyActionReq>({
    defaultValues: {
      notes: '',
      ...getCustodyActionDefaultValues(user),
    },
    mode: 'all',
  });
  const { getValues, trigger } = methods;

  const onReleaseToOwner = async () => {
    const { bulkSelection } = custodyModalValues;
    // If bulkSelection is not undefined, it means user wants to Release to Owner multiple selected Properties
    const properties =
      bulkSelection ?? (currentPropertyId ? [currentPropertyId] : []);
    const isValid = await trigger();
    if (isValid && parentId && properties) {
      const values = getValues();
      values.actionDateTime = normalizeCustodyActionDateTime(
        values.actionDateTime,
      ) as string;
      const apiFunc =
        custodyAction === CustodyActions.DISPOSE
          ? sourceAdapter.custodyDispose
          : custodyAction === CustodyActions.DESTROY
          ? sourceAdapter.custodyDestroy
          : sourceAdapter.custodyAuction;
      await apiFunc(parentId, properties, values);
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Property is released successfully.',
      });
      return true;
    }
    return false;
  };

  return (
    <FormProvider {...methods}>
      <Stack>
        <SideModalTitle>Release - {custodyAction.toLowerCase()}</SideModalTitle>
        <Grid container>
          <Grid item md={12}>
            <CustodyActionSection officerOptions={officerOptions} />
          </Grid>
          {ReleaseFormFields.map(({ name, ...rest }) => (
            <Grid key={name} item md={12}>
              <RenderFormField {...rest} name={name} />
            </Grid>
          ))}
        </Grid>
        <PropertyModalFooter onSave={onReleaseToOwner} />
      </Stack>
    </FormProvider>
  );
};
